import * as React from 'react';
import { useFormikContext } from 'formik';
import { ImageField, type ImageFieldShape } from './ImageField';

/** The shape the storefront form stores every image under. */
export interface ImageFormValue {
  file: File | null;
  changed: boolean;
  removed: boolean;
}

export interface FormikImageFieldProps {
  /** Formik key: 'logo' | 'profileImage' | 'coverImage'. */
  name: string;
  label: string;
  /** The saved image, if the seller already has one. */
  imageUrl?: string;
  requiredWidth: number;
  requiredHeight: number;
  shape?: ImageFieldShape;
  /** Square images are cropped to the middle, so say so before it happens. */
  expectSquare?: boolean;
  disabled?: boolean;
  /** The panel already names it — see ImageField. */
  hideLabel?: boolean;
  testId?: string;
}

const ACCEPT = ['image/jpeg', 'image/png', 'image/svg+xml'];

/**
 * One image field, bound to the storefront form.
 *
 * This replaces LogoUpload, ProfileImageUpload and CoverImageDropZone —
 * three components of about a hundred and sixty lines each, all running the
 * same preview/validate/revert/remove machinery with different nouns, two
 * spellings of the label, colours written as hex instead of theme tokens, and
 * a placeholder icon nearly two hundred pixels across. They also disagreed:
 * one validated dimensions before reading the file, one after; one said
 * "Profile Image", the other "Logo".
 *
 * The chosen treatment is B — the picture, then its controls underneath —
 * so nothing is ever drawn on top of a seller's photograph.
 */
export const FormikImageField: React.FC<FormikImageFieldProps> = ({
  name,
  label,
  imageUrl,
  requiredWidth,
  requiredHeight,
  shape = 'square',
  expectSquare = shape === 'square',
  disabled,
  hideLabel,
  testId,
}) => {
  const { values, setFieldValue, initialValues } = useFormikContext<Record<string, unknown>>();
  const current = values[name] as ImageFormValue | undefined;

  const [preview, setPreview] = React.useState<string | null>(imageUrl ?? null);
  const [error, setError] = React.useState<string>();
  const [warning, setWarning] = React.useState<string>();
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The saved image arriving (or the form being reset back to it) is what puts
  // the preview back; the three old components each did this with three
  // effects apiece, and disagreed about which one won.
  React.useEffect(() => {
    if (!current?.changed) {
      setPreview(imageUrl ?? null);
      setError(undefined);
      setWarning(undefined);
    }
  }, [imageUrl, current?.changed]);

  // Object URLs are a resource, not a string. The old components made one per
  // file and never released any of them.
  const objectUrl = React.useRef<string | null>(null);
  React.useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clearing the input means the same file can be picked twice running.
    event.target.value = '';
    if (!file) return;

    if (!ACCEPT.includes(file.type)) {
      setError('That file is not a JPEG, PNG or SVG.');
      return;
    }

    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    const url = URL.createObjectURL(file);
    objectUrl.current = url;

    const probe = new Image();
    probe.onload = () => {
      if (probe.width < requiredWidth || probe.height < requiredHeight) {
        // The measurements, not just the rule — "at least 500×500" leaves the
        // seller to work out why their file failed.
        setError(
          `That image is ${probe.width}×${probe.height}. It needs to be at least ${requiredWidth}×${requiredHeight}.`,
        );
        setWarning(undefined);
        return;
      }
      setError(undefined);
      setWarning(
        expectSquare && probe.width !== probe.height
          ? `This ${label.toLowerCase()} is not square, so it will be cropped to the middle.`
          : undefined,
      );
      setPreview(url);
      setFieldValue(name, { file, changed: true, removed: false });
    };
    probe.onerror = () => setError('That file could not be read as an image.');
    probe.src = url;
  };

  const handleRemove = () => {
    setPreview(null);
    setError(undefined);
    setWarning(undefined);
    setFieldValue(name, { file: null, changed: true, removed: true });
  };

  const handleRevert = () => {
    setPreview(imageUrl ?? null);
    setError(undefined);
    setWarning(undefined);
    setFieldValue(name, initialValues[name]);
  };

  return (
    <>
      <ImageField
        label={label}
        requirement={
          shape === 'wide'
            ? `${requiredWidth}×${requiredHeight} or larger`
            : `Square, ${requiredWidth}×${requiredHeight} or larger`
        }
        shape={shape}
        treatment="tile-caption"
        src={preview ?? undefined}
        disabled={disabled}
        error={error}
        warning={warning}
        onPick={() => inputRef.current?.click()}
        hideLabel={hideLabel}
        onRemove={current?.changed ? handleRevert : handleRemove}
        removeLabel={current?.changed ? 'Undo' : 'Remove'}
        testId={testId}
      />
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPT.join(',')}
        disabled={disabled}
        onChange={handleFile}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    </>
  );
};
